import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Calendar, AlertCircle, CheckCircle2, CreditCard } from "lucide-react";
import PlanCard from "./misc/PlanCard";
import ModalNew from "../../components/Modal/ModalNew";
import BankTransferModal from "../payment/misc/BankTransferModal";
import AddOnsAi from "../pricing/ai/misc/AddOnsAi";
import { toast } from "sonner";
import {
  subscriptionService,
  featuresService,
} from "../../services/api/settings";
import { useOrganizationData } from "../../services/api/settings";
import { PRICING_PLANS, getPlanByTier } from "../../constants/pricingPlans";
import { Feature } from "../../store/types/settings";
import UnsubscribeButton from "../../components/elements/UnsubscribeButton";

const RenewSubscription: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("Id");
  const { organizationData, loading: orgLoading } = useOrganizationData();
  const [billingType, setBillingType] = useState<
    "MONTHLY" | "QUARTERLY" | "YEARLY"
  >("MONTHLY");
  const [loading, setLoading] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null);
  const [paymentModal, setPaymentModal] = useState({
    isOpen: false,
    authorizationUrl: "",
  });
  const [bankTransferModal, setBankTransferModal] = useState(false);
  const [provider, setProvider] = useState<"paystack" | "paypal">("paypal");

  const currentTier = organizationData?.subscriptionTier || "STARTER";
  const subscriptionExpiry = organizationData?.subscriptionExpiry;
  const isExpired = subscriptionExpiry
    ? new Date(subscriptionExpiry) < new Date()
    : false;

  const currentPlan = getPlanByTier(currentTier);

  const [open, setOpen] = useState(false);
  const handleModalClose2 = () => setOpen(false);

  // Add-on features state
  const [features, setFeatures] = useState<Feature[]>([]);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([]);
  const [selectedDirectAddOnIds, setSelectedDirectAddOnIds] = useState<
    string[]
  >([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);

  // Fetch features on component mount
  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const orgId = organizationData?.id;
        if (!orgId) {
          console.error("Organization ID is required to fetch features");
          return;
        }
        const response = await featuresService.getAllAvailable(orgId);
        if (response.status === "success" && response.data) {
          setFeatures(response.data);
        }
      } catch (error) {
        console.error("Error fetching features:", error);
      }
    };
    fetchFeatures();
  }, [organizationData?.id]);
  // useEffect(() => {
  //   const fetchFeatures = async () => {
  //     try {
  //       setLoadingFeatures(true);
  //       const response = await featuresService.getAll();
  //       if (response.success && response.data) {
  //         setFeatures(response.data);
  //       }
  //     } catch (error) {
  //       console.error("Error fetching features:", error);
  //       toast.error("Failed to load add-on features");
  //     } finally {
  //       setLoadingFeatures(false);
  //     }
  //   };
  //   fetchFeatures();
  // }, []);

  // Calculate selected features array
  const selectedFeatures = features.filter((f) =>
    selectedFeatureIds.includes(f.id),
  );

  // Calculate total price
  const calculateTotalPrice = (): string => {
    if (!currentPlan) return "";

    const basePriceStr =
      billingType === "MONTHLY"
        ? currentPlan.priceMonthly
        : billingType === "QUARTERLY"
          ? currentPlan.priceQuarterly
          : currentPlan.priceYearly;

    // Remove currency symbol and commas, parse to number
    const basePrice = parseFloat(basePriceStr.replace(/[₦,]/g, ""));
    // Convert feature prices from kobo to naira (divide by 1000)
    const featuresTotal = selectedFeatures.reduce((sum, f) => sum + f.price, 0);

    const aiAddOnsTotal = selectedDirectAddOnIds.reduce((sum, id) => {
      const addon = features.find((a) => a.id === id);
      if (!addon) return sum;
      return sum + addon.price;
    }, 0);

    const total = basePrice + featuresTotal + aiAddOnsTotal;

    return `N${total.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Handle feature removal
  const handleFeatureRemove = (featureId: string) => {
    setSelectedFeatureIds((prev) => prev.filter((id) => id !== featureId));
  };

  const handleRenew = async (selectedProvider: "paystack" | "paypal") => {
    if (!currentPlan || loading) return;

    setLoading(true);
    setProvider(selectedProvider);

    try {
      const result = await subscriptionService.subscribe(
        currentPlan.tier,
        selectedProvider,
        billingType,
        organizationData?.id,
        selectedFeatureIds.length > 0 ? selectedFeatureIds : undefined,
        undefined,
        selectedDirectAddOnIds.length > 0 ? selectedDirectAddOnIds : undefined,
      );

      if (result.status === "success" && result.data?.authorizationUrl) {
        if (selectedProvider === "paystack") {
          setOpen(true);
          // setPaymentModal({
          //   isOpen: true,
          //   authorizationUrl: result.data.authorizationUrl,
          // });
        } else {
          window.open(result.data.authorizationUrl, "_blank");
        }
      } else {
        toast.error(`Unable to initiate ${selectedProvider} payment`);
      }
    } catch (error) {
      console.error("Renewal error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setPaymentModal({ isOpen: false, authorizationUrl: "" });
  };

  const handleBankTransfer = () => {
    setBankTransferModal(true);
  };

  const handleFeatureSelectionChange = (featureIds: string[]) => {
    setSelectedFeatureIds(featureIds);
  };

  const handleDirectAddOnToggle = (id: string) => {
    setSelectedDirectAddOnIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  if (orgLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-[#7f7f7f]/10 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#7f7f7f]/10 py-10 px-3 font-abel">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-5">
          <h1 className="text-[20px] md:text-[30px] font-medium text-[#212121]">
            Renew Your Subscription
          </h1>
          <p className="text-lg text-[#616161] max-w-2xl mx-auto">
            Continue enjoying AI-powered hiring with an extended subscription
          </p>
        </div>

        {/* Current Subscription Status */}
        <div className="max-w-4xl mx-auto mb-4">
          <div
            className={`rounded-lg p-3 border ${
              isExpired
                ? "bg-red-50 border-red-300"
                : "bg-blue-50 border-blue-300"
            }`}
          >
            <div className="flex items-start gap-2">
              {isExpired ? (
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              ) : (
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              )}
              <div className="flex-1">
                <h3
                  className={`text-[15px] font-medium mb-2 ${
                    isExpired ? "text-red-800" : "text-blue-800"
                  }`}
                >
                  {isExpired
                    ? "Your Subscription Has Expired"
                    : "Current Subscription Status"}
                </h3>
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-[14px]">
                    <span className="text-gray-700 font-medium">
                      Current Plan:
                    </span>
                    <span className="text-gray-900 font-medium">
                      {currentPlan?.name || currentTier}
                    </span>
                  </div>
                  {subscriptionExpiry && (
                    <div className="flex items-center justify-between text-[14px]">
                      <span className="text-gray-700 font-medium flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {isExpired ? "Expired On:" : "Expires On:"}
                      </span>
                      <span
                        className={`font-medium ${
                          isExpired ? "text-red-700" : "text-gray-900"
                        }`}
                      >
                        {new Date(subscriptionExpiry).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {subscriptionExpiry && (
                    <div className="flex items-center justify-end text-[14px]">
                      {isExpired ? <div></div> : <UnsubscribeButton />}
                    </div>
                  )}
                </div>
                {isExpired && (
                  <p className="mt-3 text-sm text-red-700">
                    Renew now to restore access to all features
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center bg-[gray]/10 rounded-full p-1 shadow-inner relative">
            <button
              onClick={() => setBillingType("MONTHLY")}
              className={`px-8 py-3 rounded-full transition-all ${
                billingType === "MONTHLY"
                  ? "bg-white text-[#0B60D6] shadow-md"
                  : "text-[gray]/70 hover:text-[gray]"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingType("QUARTERLY")}
              className={`px-8 py-3 rounded-full transition-all relative ${
                billingType === "QUARTERLY"
                  ? "bg-[white] text-[#0B60D6] shadow-md"
                  : "text-[gray]/70 hover:text-[gray]"
              }`}
            >
              Annual
            </button>
            <span className="absolute -top-1 right-2 px-2 py-0.5 bg-primary text-white text-[8px] rounded-full">
              Save 10%
            </span>
          </div>
        </div>

        {/* Current Plan Card */}
        {currentPlan && (
          <div className="max-w-md mx-auto mb-8 pt-1">
            <PlanCard
              {...currentPlan}
              billingType={billingType}
              isCurrent={true}
              isExpanded={expandedPlan === 0}
              onToggleExpand={() =>
                setExpandedPlan(expandedPlan === 0 ? null : 0)
              }
              onSelect={() => {}}
              selectedFeatures={selectedFeatures}
              totalPrice={
                selectedFeatures.length > 0 ? calculateTotalPrice() : undefined
              }
              onFeatureRemove={handleFeatureRemove}
            />

            {/* Payment Options */}
            <div className="mt-6 bg-white rounded-lg shadow-lg p-6 border border-[#EEEEEE]">
              <h3 className="text-lg font-medium text-[#212121] mb-4">
                Choose Payment Method
              </h3>
              <div className="space-y-3">
                <button
                  onClick={() => handleRenew("paypal")}
                  disabled={loading}
                  className="w-full py-3 px-6 bg-gradient-to-r from-[#1976D2] to-[#0B60D6] text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading && provider === "paypal" ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Processing...
                    </span>
                  ) : (
                    "Renew with PayPal"
                  )}
                </button>

                <button
                  onClick={() => handleRenew("paystack")}
                  disabled={loading}
                  className="w-full py-3 px-6 bg-[#1E88E5] text-white rounded-lg font-medium hover:bg-[#1976D2] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading && provider === "paystack" ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Processing...
                    </span>
                  ) : (
                    "Renew with Paystack"
                  )}
                </button>

                <button
                  onClick={handleBankTransfer}
                  disabled={loading}
                  className="w-full py-3 px-6 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Renew with Bank Transfer
                </button>

                <button
                  onClick={() => navigate("/subscriptions/upgrade")}
                  className="w-full py-3 px-6 border-2 border-[#1976D2] text-[#1976D2] rounded-lg font-medium hover:bg-[#E3F2FD] transition-all"
                >
                  Upgrade to a Higher Plan Instead
                </button>
              </div>
            </div>
          </div>
        )}

        <AddOnsAi
          selectedIds={selectedDirectAddOnIds}
          onToggle={handleDirectAddOnToggle}
        />
        {/* <AddOnServices
          selectedFeatureIds={selectedFeatureIds}
          onSelectionChange={handleFeatureSelectionChange}
          organizationId={organizationData?.id ?? ""}
          features={features}
          loading={loading}
        /> */}

        {/* Payment Modal */}
        <ModalNew
          isOpen={paymentModal.isOpen}
          onClose={handleModalClose}
          title="Complete Payment"
        >
          <div className="p-6">
            {paymentModal.authorizationUrl ? (
              <div className="space-y-4">
                <p className="text-gray-700 mb-4">
                  Please complete your payment to renew your subscription.
                </p>

                <div className="w-full h-96 border rounded-lg overflow-hidden">
                  <iframe
                    src={paymentModal.authorizationUrl}
                    className="w-full h-full"
                    title="Payment Gateway"
                    frameBorder="0"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    onClick={() =>
                      window.open(paymentModal.authorizationUrl, "_blank")
                    }
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
                  >
                    Open Payment Page
                  </button>
                  <button
                    onClick={handleModalClose}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p>Loading payment details...</p>
              </div>
            )}
          </div>
        </ModalNew>

        {/* Bank Transfer Modal */}
        {currentPlan && bankTransferModal && (
          <BankTransferModal
            isOpen={bankTransferModal}
            onClose={() => setBankTransferModal(false)}
            organizationId={organizationData?.id || ""}
            tier={currentPlan.tier}
            frequency={billingType}
            featureIds={selectedFeatureIds}
            directAddOnIds={selectedDirectAddOnIds}
          />
        )}

        <ModalNew
          isOpen={open}
          onClose={handleModalClose2}
          title=""
          className="max-w-[300px] mx-auto"
        >
          <div className="text-center">
            <div className="flex flex-col items-center text-center">
              <div
                className={`w-16 h-16 rounded-full bg-gradient-to-r from-[#00C3F7] to-[#0081CB] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
              >
                <CreditCard className="w-8 h-8 text-white" />
              </div>

              <h3 className="text-[18px] md:text-[30px] font-medium mb-2 text-[#212121]">
                Paystack
              </h3>

              <p className="text-gray-600 text-[16px] md:text-[21px] mb-4">
                Coming Soon
              </p>
            </div>
          </div>
        </ModalNew>
      </div>
    </div>
  );
};

export default RenewSubscription;
